import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { api, type RenderRequest, type RenderResponse } from '../../cloud/api';
import type { VoiceAppendTurn } from './useVoiceSession';

export interface AskMojiTurn {
    user: string;
    render: RenderResponse;
}

export interface UseAskMojiThreadResult {
    thread: AskMojiTurn[];
    query: string;
    submitting: boolean;
    error: string | null;
    setQuery: Dispatch<SetStateAction<string>>;
    ask: () => Promise<void>;
    renderVoice: (query: string) => Promise<RenderResponse>;
    appendTurn: (turn: VoiceAppendTurn) => void;
}

export interface UseAskMojiThreadDeps {
    render?: (req: RenderRequest) => Promise<RenderResponse>;
}

export function useAskMojiThread(
    palId: string,
    deps: UseAskMojiThreadDeps = {},
): UseAskMojiThreadResult {
    const render = deps.render ?? defaultRender;
    const [query, setQuery] = useState('');
    const [thread, setThread] = useState<AskMojiTurn[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setQuery('');
        setThread([]);
        setSubmitting(false);
        setError(null);
    }, [palId]);

    const appendTurn = useCallback((turn: VoiceAppendTurn) => {
        setThread((current) => [...current, turn]);
    }, []);

    const renderVoice = useCallback(async (rawQuery: string) => {
        const q = rawQuery.trim();
        if (!q) throw new Error('empty_query');
        return render({
            trigger: 'direct_query',
            query: q,
            thread_id: latestThreadId(thread),
            voice: true,
        });
    }, [render, thread]);

    const ask = useCallback(async () => {
        const q = query.trim();
        if (!q || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const response = await render({
                trigger: 'direct_query',
                query: q,
                thread_id: latestThreadId(thread),
            });
            setThread((current) => [...current, { user: q, render: response }]);
            setQuery('');
        } catch (err) {
            setError(formatRenderError(err));
        } finally {
            setSubmitting(false);
        }
    }, [query, render, submitting, thread]);

    return {
        thread,
        query,
        submitting,
        error,
        setQuery,
        ask,
        renderVoice,
        appendTurn,
    };
}

function defaultRender(req: RenderRequest): Promise<RenderResponse> {
    return api.render(req);
}

function latestThreadId(thread: AskMojiTurn[]): string | undefined {
    return thread.length > 0 ? thread[thread.length - 1]!.render.thread_id : undefined;
}

function formatRenderError(err: unknown): string {
    const e = err as Error & { status?: number };
    if (e.message === 'render_unavailable') {
        return 'The Pal is offline (no API key configured). Try again later.';
    }
    return e.message || 'render_failed';
}

